/**
 * Cached fetch wrapper for search APIs
 * Provides imperative caching for components that use manual fetch calls
 * 
 * This module provides cached versions of fetch() that automatically
 * cache responses with TTL and optional localStorage persistence.
 */

// In-memory cache
const searchCache = new Map();

// Pending requests (for deduplication)
const pendingRequests = new Map();

// Default TTLs
const DEFAULT_MEMORY_TTL = 5 * 60 * 1000; // 5 minutes
const DEFAULT_STORAGE_TTL = 30 * 60 * 1000; // 30 minutes

/**
 * Check if cache entry is valid
 */
const isCacheValid = (entry) => {
  return entry && entry.expiresAt > Date.now();
};

/**
 * Cached fetch for refinement API
 * Memory-only caching (5 min TTL)
 * 
 * @param {string} url - API URL
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<Response>} - Fetch response
 * 
 * @example
 * fetchRefinementsCached(refinementServlet)
 *   .then(res => res.json())
 *   .then(data => {...});
 */
export async function fetchRefinementsCached(url, options = {}) {
  const cacheKey = url;

  // Check cache first
  const cached = searchCache.get(cacheKey);
  if (isCacheValid(cached)) {
    // Return cloned response from cache
    return Promise.resolve(new Response(JSON.stringify(cached.data), {
      status: 200,
      statusText: 'OK (Cached)',
      headers: { 'Content-Type': 'application/json' }
    }));
  }

  // Check for pending request
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }

  // Make new request
  const requestPromise = fetch(url, options)
    .then(async (response) => {
      if (response.ok) {
        const data = await response.json();
        
        // Cache the result
        searchCache.set(cacheKey, {
          data,
          expiresAt: Date.now() + DEFAULT_MEMORY_TTL,
          cachedAt: Date.now(),
        });

        // Return new response with the data
        return new Response(JSON.stringify(data), {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
        });
      }
      return response;
    })
    .finally(() => {
      pendingRequests.delete(cacheKey);
    });

  pendingRequests.set(cacheKey, requestPromise);
  return requestPromise;
}

/**
 * Cached fetch for search results API
 * Memory-only caching (5 min TTL)
 * 
 * @param {string} url - API URL
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<Response>} - Fetch response
 * 
 * @example
 * fetchSearchResultsCached(sortServletURL)
 *   .then(res => res.json())
 *   .then(data => {...});
 */
export async function fetchSearchResultsCached(url, options = {}) {
  const cacheKey = url;

  // Check cache first
  const cached = searchCache.get(cacheKey);
  if (isCacheValid(cached)) {
    // Return cloned response from cache
    return Promise.resolve(new Response(JSON.stringify(cached.data), {
      status: 200,
      statusText: 'OK (Cached)',
      headers: { 'Content-Type': 'application/json' }
    }));
  }

  // Check for pending request
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }

  // Make new request
  const requestPromise = fetch(url, options)
    .then(async (response) => {
      if (response.ok) {
        const data = await response.json();
        
        // Cache the result
        searchCache.set(cacheKey, {
          data,
          expiresAt: Date.now() + DEFAULT_MEMORY_TTL,
          cachedAt: Date.now(),
        });

        // Return new response with the data
        return new Response(JSON.stringify(data), {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
        });
      }
      return response;
    })
    .finally(() => {
      pendingRequests.delete(cacheKey);
    });

  pendingRequests.set(cacheKey, requestPromise);
  return requestPromise;
}

/**
 * Cached fetch for typeahead API
 * Uses localStorage persistence (15min memory, 30min storage)
 * 
 * @param {string} url - API URL
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<Response>} - Fetch response
 * 
 * @example
 * fetchTypeaheadCached(completeSuggestURL)
 *   .then(res => res.json())
 *   .then(data => {...});
 */
export async function fetchTypeaheadCached(url, options = {}) {
  const cacheKey = url;
  // Use full base64-encoded URL for unique storage key (no truncation to avoid collisions)
  const storageKey = `dartContainer_typeahead_${btoa(url)}`;

  // Check memory cache first
  const memCached = searchCache.get(cacheKey);
  if (isCacheValid(memCached)) {
    return Promise.resolve(new Response(JSON.stringify(memCached.data), {
      status: 200,
      statusText: 'OK (Memory Cache)',
      headers: { 'Content-Type': 'application/json' }
    }));
  }

  // Check localStorage
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (isCacheValid(parsed)) {
        // Restore to memory cache
        searchCache.set(cacheKey, parsed);
        
        return Promise.resolve(new Response(JSON.stringify(parsed.data), {
          status: 200,
          statusText: 'OK (Storage Cache)',
          headers: { 'Content-Type': 'application/json' }
        }));
      } else {
        // Remove expired entry
        localStorage.removeItem(storageKey);
      }
    }
  } catch (error) {
    console.warn('Failed to read from localStorage:', error);
  }

  // Check for pending request
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }

  // Make new request
  const requestPromise = fetch(url, options)
    .then(async (response) => {
      if (response.ok) {
        const data = await response.json();
        
        const cacheEntry = {
          data,
          expiresAt: Date.now() + (15 * 60 * 1000), // 15 min memory
          cachedAt: Date.now(),
        };

        // Cache in memory
        searchCache.set(cacheKey, cacheEntry);

        // Cache in localStorage with longer TTL
        try {
          const storageEntry = {
            data,
            expiresAt: Date.now() + DEFAULT_STORAGE_TTL, // 30 min storage
            cachedAt: Date.now(),
          };
          localStorage.setItem(storageKey, JSON.stringify(storageEntry));
        } catch (error) {
          console.warn('Failed to save to localStorage:', error);
        }

        // Return new response with the data
        return new Response(JSON.stringify(data), {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
        });
      }
      return response;
    })
    .finally(() => {
      pendingRequests.delete(cacheKey);
    });

  pendingRequests.set(cacheKey, requestPromise);
  return requestPromise;
}

/**
 * Clear all search caches
 */
export function clearSearchCache() {
  searchCache.clear();
  pendingRequests.clear();
  
  // Clear typeahead from localStorage
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('dartContainer_typeahead_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.warn('Failed to clear localStorage:', error);
  }
}

/**
 * Get cache statistics
 */
export function getSearchCacheStats() {
  let validEntries = 0;
  
  searchCache.forEach(entry => {
    if (isCacheValid(entry)) {
      validEntries++;
    }
  });

  return {
    totalEntries: searchCache.size,
    validEntries,
    expiredEntries: searchCache.size - validEntries,
    pendingRequests: pendingRequests.size,
  };
}
